<script lang="ts" setup>
import { type Component } from "vue";
import { ArrowDown, ArrowDownUp, ArrowUp } from "@lucide/vue";

import { Order_By } from "@/graphql";
import { ProjectListSortOption } from "../constants/SortOptions";
import ProjectTableItemSkeleton from "./ProjectTableItemSkeleton.vue";

type SortField = ProjectListSortOption["orderBy"]["field"];

const props = defineProps<{
  loading?: boolean;
  skeletonCount?: number;
  sortDirection?: Order_By;
  sortField?: SortField | null;
}>();

const emit = defineEmits<{
  (e: "sort", field: SortField): void;
}>();

const isActive = (field: SortField): boolean => props.sortField === field;

const getSortIcon = (field: SortField): Component => {
  if (!isActive(field)) return ArrowDownUp;
  return props.sortDirection === Order_By.asc_nulls_last ? ArrowUp : ArrowDown;
};
</script>

<template>
  <!-- No overflow-hidden on the frame: the row action menu renders in flow and hangs below the last row,
       and a clipped frame is also a scroll container that scrolled the rows out of view when the old
       kebab focused its first item (RTLCA-5609). The header and the last row round their own corners. -->
  <div class="sm:rounded-16 border border-slate-200">
    <!-- Semantic table built with role attributes; using real <table> wraps each row in an anonymous <td>, breaking display:grid layout. -->
    <div
      role="table"
      class="t-small w-full text-left">
      <div
        role="row"
        class="t-caption-bold text-type-muted sm:rounded-t-16 grid w-full grid-cols-17 items-center gap-2 border-b border-slate-200 bg-slate-100 px-4 py-2.5 tracking-wide uppercase">
        <!-- Compare checkbox column. The label is sr-only: "Compare" doesn't fit 1/17 of the row and
             overflowed into Name, and a select column conventionally has no visible header. Each row's
             checkbox carries its own aria-label. -->
        <span class="col-span-1"><span class="sr-only">Compare</span></span>

        <!-- Name: sortable -->
        <span
          class="group col-span-3 flex cursor-pointer items-center gap-1 transition-colors select-none"
          :class="isActive('name') ? 'text-type-default' : 'hover:text-type-default'"
          role="button"
          tabindex="0"
          @click="emit('sort', 'name')"
          @keydown.enter="emit('sort', 'name')"
          @keydown.space.prevent="emit('sort', 'name')">
          Name
          <component
            :is="getSortIcon('name')"
            class="icon-xs shrink-0 transition-opacity"
            :stroke-width="1.5"
            :class="isActive('name') ? 'opacity-100' : 'opacity-40 group-hover:opacity-100'" />
        </span>

        <!-- Last Updated: sortable -->
        <span
          class="group col-span-2 flex cursor-pointer items-center gap-1 transition-colors select-none"
          :class="isActive('updatedAt') ? 'text-type-default' : 'hover:text-type-default'"
          role="button"
          tabindex="0"
          @click="emit('sort', 'updatedAt')"
          @keydown.enter="emit('sort', 'updatedAt')"
          @keydown.space.prevent="emit('sort', 'updatedAt')">
          Last updated
          <component
            :is="getSortIcon('updatedAt')"
            class="icon-xs shrink-0 transition-opacity"
            :stroke-width="1.5"
            :class="isActive('updatedAt') ? 'opacity-100' : 'opacity-40 group-hover:opacity-100'" />
        </span>

        <!-- Mapping complete: NOT sortable -->
        <span class="col-span-2"> Mapping complete </span>

        <!-- Threshold and Used: NOT sortable. The unit differs per project, so each row carries its own (RTLCA-5612). -->
        <span class="col-span-2"> Threshold </span>
        <span class="col-span-2"> Used </span>

        <!-- m²: sortable -->
        <span
          class="group col-span-2 flex cursor-pointer items-center gap-1 transition-colors select-none"
          :class="isActive('area') ? 'text-type-default' : 'hover:text-type-default'"
          role="button"
          tabindex="0"
          @click="emit('sort', 'area')"
          @keydown.enter="emit('sort', 'area')"
          @keydown.space.prevent="emit('sort', 'area')">
          m&#178;
          <component
            :is="getSortIcon('area')"
            class="icon-xs shrink-0 transition-opacity"
            :stroke-width="1.5"
            :class="isActive('area') ? 'opacity-100' : 'opacity-40 group-hover:opacity-100'" />
        </span>

        <!-- Type: sortable -->
        <span
          class="group col-span-2 flex cursor-pointer items-center gap-1 transition-colors select-none"
          :class="isActive('buildingType') ? 'text-type-default' : 'hover:text-type-default'"
          role="button"
          tabindex="0"
          @click="emit('sort', 'buildingType')"
          @keydown.enter="emit('sort', 'buildingType')"
          @keydown.space.prevent="emit('sort', 'buildingType')">
          Type
          <component
            :is="getSortIcon('buildingType')"
            class="icon-xs shrink-0 transition-opacity"
            :stroke-width="1.5"
            :class="isActive('buildingType') ? 'opacity-100' : 'opacity-40 group-hover:opacity-100'" />
        </span>

        <!-- Actions: spacer -->
        <span></span>
      </div>
      <div role="rowgroup">
        <ProjectTableItemSkeleton
          v-for="i in skeletonCount || 100"
          v-if="loading"
          :key="i" />
        <slot v-else />
      </div>
    </div>
  </div>
</template>
